import React, { useMemo } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { networkReportOrganizationsPath } from "../networkReportPaths";
import NetworkOrganizationQuiltCard from "./NetworkOrganizationQuiltCard";

const STATUS_PRIORITY = {
  in_progress: 0,
  not_started: 1,
  no_survey: 2,
  completed: 3,
};
export const orderOrganizationsForQuilt = (organizations = []) =>
  [...organizations].sort((left, right) => {
    const statusDifference =
      (STATUS_PRIORITY[left.status] ?? Number.MAX_SAFE_INTEGER) -
      (STATUS_PRIORITY[right.status] ?? Number.MAX_SAFE_INTEGER);

    return statusDifference ||
      left.organizationName.localeCompare(right.organizationName);
  });

const NetworkOrganizationQuilt = ({
  organizations = [],
  loading = false,
  error = false,
  networkId,
}) => {
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const orderedOrganizations = useMemo(
    () => orderOrganizationsForQuilt(organizations),
    [organizations]
  );

  return (
    <Box>
      <Stack spacing={4}>
        <Flex
          direction={{ base: "column", md: "row" }}
          align={{ base: "stretch", md: "start" }}
          justify="space-between"
          gap={4}
        >
          <Box>
            <Heading size="sm">Schools at a glance</Heading>
            <Text mt={1} color={mutedColor} fontSize="sm">
              Compare strengths and development areas across the Network.
            </Text>
          </Box>
          <Button
            as={RouterLink}
            to={networkReportOrganizationsPath(networkId)}
            size="sm"
            variant="outline"
            width={{ base: "100%", md: "auto" }}
          >
            View all schools
          </Button>
        </Flex>

        {loading ? (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4, "2xl": 5 }} spacing={2.5}>
            {Array.from({ length: 5 }, (_, index) => (
              <Skeleton
                key={index}
                height="190px"
                borderRadius="md"
                aria-label="Loading school overview"
              />
            ))}
          </SimpleGrid>
        ) : error ? (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            School overview is temporarily unavailable.
          </Alert>
        ) : organizations.length === 0 ? (
          <Box py={4}>
            <Heading size="xs">No schools</Heading>
            <Text mt={1} color={mutedColor}>
              No schools are available in this Network yet.
            </Text>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4, "2xl": 5 }} spacing={2.5}>
            {orderedOrganizations.map((organization) => (
              <NetworkOrganizationQuiltCard
                key={organization.id}
                organization={organization}
                networkId={networkId}
              />
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </Box>
  );
};

export default NetworkOrganizationQuilt;
